import { format, isValid, parse } from 'date-fns'

export const INPUT_FIELD_DATE_DISPLAY_FORMAT = 'MM / dd / yyyy'

export type InputFieldDateParts = {
  month: string
  day: string
  year: string
}

export const formatInputFieldDate = (date: Date): string =>
  format(date, INPUT_FIELD_DATE_DISPLAY_FORMAT)

export const getInputFieldDateParts = (date: Date): InputFieldDateParts => ({
  month: format(date, 'MM'),
  day: format(date, 'dd'),
  year: format(date, 'yyyy'),
})

export const parseInputFieldDate = (value: string): Date | null => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }

  const parsed = parse(trimmed, INPUT_FIELD_DATE_DISPLAY_FORMAT, new Date())
  return isValid(parsed) ? parsed : null
}

export const INPUT_FIELD_CARD_NUMBER_MAX_DIGITS = 16

export const INPUT_FIELD_CARD_NUMBER_FORMATTED_MAX_LENGTH = 19

export const normalizeInputFieldCardNumberDigits = (value: string): string =>
  value.replace(/\D/g, '').slice(0, INPUT_FIELD_CARD_NUMBER_MAX_DIGITS)

export const formatInputFieldCardNumber = (value: string): string => {
  const digits = normalizeInputFieldCardNumberDigits(value)
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
}

export const INPUT_FIELD_US_PHONE_MAX_DIGITS = 10

export const INPUT_FIELD_US_PHONE_FORMATTED_MAX_LENGTH = 14

export const normalizeInputFieldPhoneDigits = (value: string): string =>
  value.replace(/\D/g, '').slice(0, INPUT_FIELD_US_PHONE_MAX_DIGITS)

export const formatInputFieldPhone = (value: string): string => {
  const digits = normalizeInputFieldPhoneDigits(value)

  if (digits.length === 0) {
    return ''
  }

  if (digits.length <= 3) {
    return `(${digits}`
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export const normalizeInputFieldAmount = (value: string): string => {
  const cleaned = value.replace(/,/g, '').replace(/[^\d.]/g, '')
  const [whole = '', ...fractionParts] = cleaned.split('.')
  const fraction = fractionParts.join('').slice(0, 2)

  if (cleaned.includes('.')) {
    if (fraction.length > 0) {
      return `${whole}.${fraction}`
    }

    return cleaned.endsWith('.') ? `${whole}.` : whole
  }

  return whole
}

export const formatInputFieldAmount = (value: string): string => {
  const normalized = normalizeInputFieldAmount(value)

  if (!normalized) {
    return ''
  }

  const hasDecimal = normalized.includes('.')
  const [whole = '', fraction = ''] = normalized.split('.')
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  if (!hasDecimal) {
    return formattedWhole
  }

  if (normalized.endsWith('.') && !fraction) {
    return `${formattedWhole}.`
  }

  return `${formattedWhole}.${fraction}`
}

export const INPUT_FIELD_VERIFICATION_MAX_DIGITS = 6

export const normalizeInputFieldVerificationDigits = (value: string): string =>
  value.replace(/\D/g, '').slice(0, INPUT_FIELD_VERIFICATION_MAX_DIGITS)

export const INPUT_FIELD_EMAIL_INVALID_MESSAGE =
  'Please enter a valid email address.'

export const normalizeInputFieldEmail = (value: string): string =>
  value.replace(/\s/g, '').replace(/[^a-zA-Z0-9@._+-]/g, '')

export const normalizeInputFieldNumberDigits = (value: string): string =>
  value.replace(/\D/g, '')

export const parseInputFieldNumber = (value: string): number | null => {
  const normalized = normalizeInputFieldNumberDigits(value)

  if (!normalized) {
    return null
  }

  const parsed = Number.parseInt(normalized, 10)
  return Number.isNaN(parsed) ? null : parsed
}

export const clampInputFieldNumber = (
  value: number,
  min?: number,
  max?: number,
): number => {
  let nextValue = value

  if (min != null) {
    nextValue = Math.max(min, nextValue)
  }

  if (max != null) {
    nextValue = Math.min(max, nextValue)
  }

  return nextValue
}

export const isValidInputFieldEmail = (value: string): boolean => {
  const trimmed = value.trim()

  if (!trimmed) {
    return false
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

export {
  formatTimePickerValue as formatInputFieldTime,
  getTimePickerParts as getInputFieldTimeParts,
  parseTimePickerValue as parseInputFieldTime,
} from '../TimePicker/TimePicker.utils'

export type { TimePickerParts as InputFieldTimeParts } from '../TimePicker/TimePicker.utils'
export type { TimePickerPeriod as InputFieldTimePeriod } from '../TimePicker/TimePicker.types'
